// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import { compose } from 'redux';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import debounce from 'lodash/debounce';
import logger from '@tcp/core/src/utils/loggerInstance';
import {
  getABTestIsSearchEnhancementRedesignEnabled,
  getABTestIsEnhancedWebSearchBarEnabled,
  getABTestIsSearchBarAutosuggestProductsEnabled,
  getABTestForCMSTrendingSearches,
} from '@tcp/core/src/reduxStore/selectors/session.selectors';
import {
  getSearchResult,
  setShowMoreProductFlag,
  getTrendingSearchResult,
  fetchTrendingSearchProducts,
} from '@tcp/core/src/components/common/molecules/SearchBar/SearchBar.actions';
import useDynamicSaga from '@tcp/core/src/components/common/hoc/withDynamicSaga';
import withDynamicReducer from '@tcp/core/src/components/common/hoc/withDynamicReducer';
import { getRecentStoreFromLocalStorage } from '@tcp/core/src/components/common/molecules/SearchBar/userRecentStore';
import { getCurrencyAttributes } from '@tcp/core/src/reduxStore/selectors/currency.selectors';
import { unsetPLPBreadCrumb } from '@tcp/core/src/components/features/browse/ProductListing/container/ProductListing.actions';
import { getLatestSearchResultsExists } from '@tcp/core/src/components/features/browse/SearchDetail/container/SearchDetail.util';
import { getCMSTrendingResults } from '@tcp/core/src/components/common/molecules/SearchBar/WebSearchBarFlyoutView/helpers/webSearchUiHelper';
import NewSearchBarReducer from './NewSearchBar.reducer';
import NewSearchBarSaga from './NewSearchBar.saga';

const NewSearchBar = dynamic(() =>
  import(
    // eslint-disable-next-line extra-rules/no-commented-out-code
    /* webpackChunkName: "new-search-bar" */ '../views/NewSearchBar.view'
  )
);
const NewSearchBarV2 = dynamic(() =>
  import(
    // eslint-disable-next-line extra-rules/no-commented-out-code
    /* webpackChunkName: "new-search-bar-v2" */ '../views/NewSearchBarV2.view'
  )
);

class NewSearchBarContainer extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      ...props,
    };
  }

  getLatestSearchResults = () => {
    const getRecentStore = getRecentStoreFromLocalStorage();
    let latestSearchResults = null;

    if (getRecentStore) {
      try {
        latestSearchResults = JSON.parse(getRecentStore.split(','));
      } catch (ex) {
        logger.error({
          error: ex,
          extraData: {
            componentName: 'SearchBar View',
            methodName: 'getLatestSearchResults',
            propsRecieved: getRecentStore,
          },
        });
      }
    }
    return latestSearchResults;
  };

  render() {
    const {
      showProduct,
      searchResults,
      startSearch,
      closeNavDrawer,
      trendingResults,
      currencyAttributes,
      trackAnalyticsClick,
      startTrendingSearch,
      toggleSearchResults,
      cmsTrendingSearches,
      trendingSearchProducts,
      isHeaderRedesignEnabled,
      unsetPLPBreadCrumbAction,
      getTrendingSearchProducts,
      isEnhancedWebSearchBarEnabled,
      isSearchEnhancementRedesignEnabled,
      isSearchBarAutosuggestProductsEnabled,
    } = this.props;
    const latestSearchResults = this.getLatestSearchResults();
    const trendingSearch = Object.keys(trendingResults || {}).length;
    const cmsTrendingSearchResults = getCMSTrendingResults(cmsTrendingSearches);
    const isLatestSearchResultsExists = getLatestSearchResultsExists(latestSearchResults);
    const Component = isHeaderRedesignEnabled ? NewSearchBarV2 : NewSearchBar;

    return (
      <Component
        showProduct={showProduct}
        startSearch={startSearch}
        searchResults={searchResults}
        closeNavDrawer={closeNavDrawer}
        trendingResults={trendingResults}
        trendingSearch={trendingSearch}
        currencyAttributes={currencyAttributes}
        cmsTrendingSearches={cmsTrendingSearches}
        trackAnalyticsClick={trackAnalyticsClick}
        latestSearchResults={latestSearchResults}
        startTrendingSearch={startTrendingSearch}
        toggleSearchResults={toggleSearchResults}
        trendingSearchProducts={trendingSearchProducts}
        unsetPLPBreadCrumbAction={unsetPLPBreadCrumbAction}
        cmsTrendingSearchResults={cmsTrendingSearchResults}
        getTrendingSearchProducts={getTrendingSearchProducts}
        isLatestSearchResultsExists={isLatestSearchResultsExists}
        isEnhancedWebSearchBarEnabled={isEnhancedWebSearchBarEnabled}
        isSearchEnhancementRedesignEnabled={isSearchEnhancementRedesignEnabled}
        isSearchBarAutosuggestProductsEnabled={isSearchBarAutosuggestProductsEnabled}
      />
    );
  }
}

NewSearchBarContainer.propTypes = {
  startSearch: PropTypes.func.isRequired,
  showProduct: PropTypes.bool,
  searchResults: PropTypes.shape({
    trends: PropTypes.shape({}),
    categories: PropTypes.shape({}),
    products: PropTypes.shape({}),
  }),
  trackAnalyticsClick: PropTypes.func,
  trendingResults: PropTypes.shape({}),
  isHeaderRedesignEnabled: PropTypes.bool,
  trendingSearchProducts: PropTypes.shape({}),
  isSearchBarAutosuggestProductsEnabled: PropTypes.bool,
};

NewSearchBarContainer.defaultProps = {
  showProduct: false,
  searchResults: {
    trends: {},
    categories: {},
    products: {},
  },
  trackAnalyticsClick: () => {},
  trendingResults: {},
  trendingSearchProducts: {},
  isHeaderRedesignEnabled: false,
  isSearchBarAutosuggestProductsEnabled: false,
};

const mapStateToProps = (state) => {
  return {
    searchResults: state?.Search?.searchResults,
    showProduct: state?.Search?.showProduct,
    isSearchEnhancementRedesignEnabled: getABTestIsSearchEnhancementRedesignEnabled(state),
    currencyAttributes: getCurrencyAttributes(state),
    isEnhancedWebSearchBarEnabled: getABTestIsEnhancedWebSearchBarEnabled(state),
    isSearchBarAutosuggestProductsEnabled: getABTestIsSearchBarAutosuggestProductsEnabled(state),
    trendingResults: state?.Search?.trendingResults,
    trendingSearchProducts: state?.Search?.trendingSearchProducts,
    cmsTrendingSearches: getABTestForCMSTrendingSearches(state),
  };
};

export const mapDispatchToProps = (dispatch) => {
  return {
    startSearch: debounce((payload) => {
      dispatch(getSearchResult(payload));
    }, 500),
    startTrendingSearch: debounce((payload) => {
      dispatch(getTrendingSearchResult(payload));
    }, 500),
    toggleSearchResults: (payload) => {
      dispatch(setShowMoreProductFlag(payload));
    },
    unsetPLPBreadCrumbAction: (payload) => {
      dispatch(unsetPLPBreadCrumb(payload));
    },
    getTrendingSearchProducts: (payload) => {
      dispatch(fetchTrendingSearchProducts(payload));
    },
  };
};

const withReducer = withDynamicReducer({ key: 'NewSearchBar', reducer: NewSearchBarReducer });
const withSaga = useDynamicSaga({ key: 'NewSearchBarSaga', saga: NewSearchBarSaga });
const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default compose(withReducer, withSaga, withConnect)(NewSearchBarContainer);
